import { useMemo, useState, useEffect, useRef } from 'react';
import {
  Divider,
  Menu,
  MenuItem,
  MenuList,
  StylesProvider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@material-ui/core';
import styled from 'styled-components';
import { ArrowDownward, ArrowUpward } from '@material-ui/icons';
import { useTable, useSortBy } from 'react-table';
import { useDispatch } from 'react-redux';
import { updateWidgetConfig } from 'shared/state/ducks/sites';
import { realignPerInstancePropertiesByIndex } from './RepeaterUtils';
import { getSignedUrl as fetchSignedUrl } from 'shared/api/s3';
import { uploadFileWithSignedUrl } from 'shared/util/s3Upload.util';
import URI from 'urijs';
import config from 'builder/config';
import { CircularProgress } from '@material-ui/core';
import { toast } from 'react-toastify';

const StyledTableCellHeader = styled(TableCell)`
  padding: 10px;
  font-family: 'Optum Sans';
  font-weight: bold;
`;

const StyledTableCellBody = styled(TableCell)`
  padding: 10px;
  font-family: 'Optum Sans';
`;

const StyledMenuItem = styled(MenuItem)`
  display: block;
  font-family: 'Optum Sans';
  font-size: 15px;
`;

const ImageCellWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
`;

const ImagePreview = styled.img`
  max-width: 60px;
  max-height: 40px;
  object-fit: cover;
  border-radius: 4px;
`;

const UploadButton = styled.label`
  cursor: pointer;
  color: #002677;
  font-size: 12px;
  font-family: 'Optum Sans';
  text-decoration: underline;
  &:hover {
    color: #001a5c;
  }
`;

/**
 * Inline image upload cell for repeater data table image columns.
 * Handles S3 upload and returns CDN URL into the cell value.
 */
const ImageCellUploader = ({ value, siteId, onChange, dataTestId }) => {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast('Invalid file type. Please select an image.', {
        type: 'error',
        theme: 'colored',
      });
      return;
    }

    setUploading(true);
    try {
      const signed = await uploadFileWithSignedUrl(
        file,
        (f, cb) => fetchSignedUrl(f, cb, siteId),
        { 'Content-Type': file.type || 'application/octet-stream' }
      );
      const uri = URI(signed.signedUrl);
      const cdnUrl = uri
        .hostname(config.cdn)
        .query('')
        .toString();
      onChange(cdnUrl);
      toast('Image uploaded successfully', {
        type: 'success',
        theme: 'colored',
      });
    } catch (err) {
      toast('Error uploading image', { type: 'error', theme: 'colored' });
    } finally {
      setUploading(false);
      if (inputRef.current) {
        inputRef.current.value = '';
      }
    }
  };

  return (
    <ImageCellWrapper>
      {value && (
        <ImagePreview
          data-test-id={`${dataTestId}-preview`}
          src={value}
          alt="uploaded"
        />
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        data-test-id={`${dataTestId}-file-input`}
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />
      {uploading ? (
        <CircularProgress size={14} />
      ) : (
        <UploadButton
          data-test-id={`${dataTestId}-upload-button`}
          onClick={() => inputRef.current?.click()}
        >
          {value ? 'Replace Image' : 'Upload Image'}
        </UploadButton>
      )}
    </ImageCellWrapper>
  );
};

//dynamic table
const RepeaterDataTable = ({
  jsonData,
  setRepeaterData,
  setContainerCount,
  onDeleteRow,
  onInsertRow,
  site,
  widgetState,
  currentPage,
  imageColumns = [] as string[],
  onImageColumnsChange,
}) => {
  const dispatch = useDispatch();
  const headers = jsonData?.headers ?? [];
  const jsonRows = jsonData?.data ?? [];
  const dataFromJson = useMemo(
    () => jsonRows.map(({ index, ...rest }) => rest),
    [jsonRows]
  );
  const [data, setData] = useState(dataFromJson);
  const [columns, setColumns] = useState(
    headers
      .filter(header => header.toLowerCase() !== 'index')
      .map(header => ({
        Header: header,
        accessor: header,
      }))
  );
  const [rowMenuAnchor, setRowMenuAnchor] = useState(null);
  const [columnMenuAnchor, setColumnMenuAnchor] = useState(null);
  const [selectedRowIndex, setSelectedRowIndex] = useState(null);
  const [selectedColumnIndex, setSelectedColumnIndex] = useState(null);
  // Ref to track whether data was modified directly (addRow, cellChange, etc.)
  // When true, the sortedData effect should skip dispatching since the caller
  // already dispatched updateRepeaterData.
  const dataModifiedRef = useRef(false);

  const handleRowContextMenu = (event, rowIndex) => {
    event.preventDefault();
    setSelectedRowIndex(rowIndex);
    setRowMenuAnchor(event.currentTarget);
  };

  const handleColumnContextMenu = (event, columnIndex) => {
    event.preventDefault();
    setSelectedColumnIndex(columnIndex);
    setColumnMenuAnchor(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setRowMenuAnchor(null);
    setColumnMenuAnchor(null);
  };

  const addRow = () => {
    const newRow = columns.reduce((acc, column) => {
      acc[column.accessor] = '';
      return acc;
    }, {});
    const insertIndex =
      selectedRowIndex != null ? selectedRowIndex + 1 : data.length;
    const updatedData = [
      ...data.slice(0, insertIndex),
      newRow,
      ...data.slice(insertIndex),
    ];
    dataModifiedRef.current = true;
    setData(updatedData);
    handleCloseMenu();
    if (onInsertRow && insertIndex < data.length) {
      onInsertRow(insertIndex);
    }
    updateContainerCountAndRepeaterData(
      columns,
      updatedData,
      updatedData.length
    );
  };

  const updateRepeaterData = (columns, data, perInstancePropsSnapshot?) => {
    const headersArray = columns.map(column => column.Header);
    headersArray.push('index');
    const updatedData = data.map((row, index) => ({
      ...row,
      index: index + 1,
    }));

    setRepeaterData({
      headers: headersArray,
      data: updatedData,
    });

    dispatch(
      updateWidgetConfig(
        site?.siteId,
        currentPage?.pageId,
        widgetState.id,
        {
          ...widgetState.config,
          containerCount: updatedData.length,
          repeaterData: {
            headers: headersArray,
            data: updatedData,
          },
          // Fold the permuted per-row snapshot into this same write so it is
          // persisted together with the reordered data (never in a separate
          // dispatch that would spread a stale config and revert the reorder).
          ...(perInstancePropsSnapshot ? { perInstancePropsSnapshot } : {}),
        },
        false
      )
    );
  };

  const updateContainerCountAndRepeaterData = (
    columns,
    data,
    containerCount
  ) => {
    const headersArray = columns.map(column => column.Header);
    headersArray.push('index');
    const updatedData = data.map((row, index) => ({
      ...row,
      index: index + 1,
    }));

    setRepeaterData({
      headers: headersArray,
      data: updatedData,
    });
    setContainerCount(containerCount);
  };

  const deleteRow = () => {
    const newData = data.filter((_, index) => index !== selectedRowIndex);
    dataModifiedRef.current = true;
    setData(newData);
    handleCloseMenu();
    if (onDeleteRow) {
      onDeleteRow(selectedRowIndex);
    }
    updateContainerCountAndRepeaterData(columns, newData, newData.length);
  };

  const moveRowUp = () => {
    if (selectedRowIndex > 0) {
      const newData = [...data];
      [newData[selectedRowIndex - 1], newData[selectedRowIndex]] = [
        newData[selectedRowIndex],
        newData[selectedRowIndex - 1],
      ];
      dataModifiedRef.current = true;
      setData(newData);
      const permutedSnapshot = realignPerInstancePropertiesByIndex({
        newToOld: {
          [selectedRowIndex - 1]: selectedRowIndex,
          [selectedRowIndex]: selectedRowIndex - 1,
        },
        repeaterDataMapping: widgetState.config?.repeaterDataMapping || {},
        currentPage,
        dispatch,
        siteId: site?.siteId,
        pageId: currentPage?.pageId,
        widgetId: widgetState.id,
      });
      updateRepeaterData(columns, newData, permutedSnapshot);
    }
    handleCloseMenu();
  };

  const moveRowDown = () => {
    if (selectedRowIndex < data.length - 1) {
      const newData = [...data];
      [newData[selectedRowIndex + 1], newData[selectedRowIndex]] = [
        newData[selectedRowIndex],
        newData[selectedRowIndex + 1],
      ];
      dataModifiedRef.current = true;
      setData(newData);
      const permutedSnapshot = realignPerInstancePropertiesByIndex({
        newToOld: {
          [selectedRowIndex]: selectedRowIndex + 1,
          [selectedRowIndex + 1]: selectedRowIndex,
        },
        repeaterDataMapping: widgetState.config?.repeaterDataMapping || {},
        currentPage,
        dispatch,
        siteId: site?.siteId,
        pageId: currentPage?.pageId,
        widgetId: widgetState.id,
      });
      updateRepeaterData(columns, newData, permutedSnapshot);
    }
    handleCloseMenu();
  };

  const addColumn = () => {
    const newColumn = `New Column ${columns.length + 1}`;
    const insertIndex =
      selectedColumnIndex != null ? selectedColumnIndex + 1 : columns.length;
    const updatedColumns = [
      ...columns.slice(0, insertIndex),
      { Header: newColumn, accessor: newColumn },
      ...columns.slice(insertIndex),
    ];
    const updatedRows = data.map(row => ({ ...row, [newColumn]: '' }));
    setColumns(updatedColumns);
    dataModifiedRef.current = true;
    setData(updatedRows);
    handleCloseMenu();
    updateRepeaterData(updatedColumns, updatedRows);
  };

  const deleteColumn = () => {
    const newColumns = columns.filter(
      (_, index) => index !== selectedColumnIndex
    );
    setColumns(newColumns);
    const newData = data.map(row => {
      const newRow = { ...row };
      delete newRow[columns[selectedColumnIndex].accessor];
      return newRow;
    });
    dataModifiedRef.current = true;
    setData(newData);
    updateRepeaterData(newColumns, newData);
    handleCloseMenu();
  };

  const moveColumnLeft = () => {
    if (selectedColumnIndex > 0) {
      const newColumns = [...columns];
      [newColumns[selectedColumnIndex - 1], newColumns[selectedColumnIndex]] = [
        newColumns[selectedColumnIndex],
        newColumns[selectedColumnIndex - 1],
      ];
      setColumns(newColumns);
      dataModifiedRef.current = true;
      updateRepeaterData(newColumns, data);
    }
    handleCloseMenu();
  };

  const moveColumnRight = () => {
    if (selectedColumnIndex < columns.length - 1) {
      const newColumns = [...columns];
      [newColumns[selectedColumnIndex + 1], newColumns[selectedColumnIndex]] = [
        newColumns[selectedColumnIndex],
        newColumns[selectedColumnIndex + 1],
      ];
      setColumns(newColumns);
      dataModifiedRef.current = true;
      updateRepeaterData(newColumns, data);
    }
    handleCloseMenu();
  };

  const handleCellChange = (rowIndex, columnId, value) => {
    const newData = [...data];
    newData[rowIndex] = { ...newData[rowIndex], [columnId]: value };
    dataModifiedRef.current = true;
    setData(newData);
    updateRepeaterData(columns, newData);
  };

  const handleColumnChange = (columnIndex, value) => {
    const oldAccessor = columns[columnIndex].accessor;

    if (value === oldAccessor) return;

    const newColumns = [...columns];
    newColumns[columnIndex].Header = value;
    newColumns[columnIndex].accessor = value;
    setColumns(newColumns);

    const updatedData = data.map(row => {
      const newRow = { ...row };
      if (newRow.hasOwnProperty(oldAccessor)) {
        newRow[value] = newRow[oldAccessor];
        delete newRow[oldAccessor];
      }
      return newRow;
    });

    dataModifiedRef.current = true;
    setData(updatedData);
    updateRepeaterData(newColumns, updatedData);
  };

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
  } = useTable(
    {
      columns,
      data,
      disableSortRemove: true,
    },
    useSortBy
  );

  const sortedData = useMemo(() => {
    return rows.map(row => row.original);
  }, [rows]);

  useEffect(() => {
    // Only dispatch when sort order changes via column header click.
    // Skip when data was modified directly (addRow, deleteRow, cellChange, etc.)
    // since those operations already call updateRepeaterData explicitly.
    if (dataModifiedRef.current) {
      dataModifiedRef.current = false;
      return;
    }

    // Detect whether the sort changed the display order (react-table's
    // row.index is the row's position in the original data array).
    let orderChanged = false;
    rows.forEach((row, newIdx) => {
      if (row.index !== newIdx) orderChanged = true;
    });

    if (orderChanged) {
      // Map each NEW sorted position to the ORIGINAL data index that moved
      // there — the same permutation model used by move up/down.
      const newToOld: Record<number, number> = {};
      rows.forEach((row, newIdx) => {
        newToOld[newIdx] = row.index;
      });

      // Guard the follow-up sortedData effect run triggered by setData below.
      dataModifiedRef.current = true;
      setData(sortedData);

      // Realign per-instance link props so they follow the data into their new
      // sorted positions, and permute the persisted snapshot. The container->row
      // mapping is intentionally left UNCHANGED (identity) so the containers
      // REFLECT the sorted order — exactly like move up/down.
      //
      // The previous logic instead remapped the mapping so each container
      // "followed its data", which (a) left a non-identity mapping so the canvas
      // did not reflect the sort, and (b) broke a subsequent move up/down that
      // relies on the identity mapping.
      const permutedSnapshot = realignPerInstancePropertiesByIndex({
        newToOld,
        repeaterDataMapping: widgetState.config?.repeaterDataMapping || {},
        currentPage,
        dispatch,
        siteId: site?.siteId,
        pageId: currentPage?.pageId,
        widgetId: widgetState.id,
      });

      updateRepeaterData(columns, sortedData, permutedSnapshot);
    } else {
      updateRepeaterData(columns, sortedData);
    }
  }, [sortedData]);

  return (
    <StylesProvider injectFirst>
      <Table {...getTableProps()}>
        <TableHead>
          {headerGroups.map(headerGroup => (
            <TableRow {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column, columnIndex) => (
                <StyledTableCellHeader
                  {...column.getHeaderProps(column.getSortByToggleProps())}
                  onContextMenu={e => handleColumnContextMenu(e, columnIndex)}
                >
                  <div
                    data-test-id={`repeater-header-${columnIndex}`}
                    contentEditable
                    suppressContentEditableWarning
                    onContextMenu={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleColumnContextMenu(e, columnIndex);
                    }}
                    onBlur={e =>
                      handleColumnChange(columnIndex, e.target.innerText)
                    }
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    {column.render('Header')}
                    <span>
                      {column.isSorted ? (
                        column.isSortedDesc ? (
                          <ArrowDownward fontSize="small" />
                        ) : (
                          <ArrowUpward fontSize="small" />
                        )
                      ) : (
                        <ArrowDownward
                          fontSize="small"
                          style={{ opacity: 0.3 }}
                        />
                      )}
                    </span>
                  </div>
                </StyledTableCellHeader>
              ))}
              <StyledTableCellHeader>
                <div
                  data-test-id="repeater-add-column"
                  onClick={addColumn}
                  style={{ cursor: 'pointer', fontWeight: 'bold' }}
                >
                  {' '}
                  + Add Column
                </div>
              </StyledTableCellHeader>
            </TableRow>
          ))}
        </TableHead>
        <Divider style={{ color: 'orange' }} />
        <TableBody {...getTableBodyProps()}>
          {rows.map((row, rowIndex) => {
            prepareRow(row);
            return (
              <TableRow {...row.getRowProps()}>
                {row.cells.map(cell => (
                  <StyledTableCellBody
                    {...cell.getCellProps()}
                    data-test-id={`repeater-cell-${rowIndex}-${cell.column.id}`}
                    onContextMenu={e => handleRowContextMenu(e, rowIndex)}
                  >
                    {imageColumns.includes(cell.column.id) ? (
                      <ImageCellUploader
                        value={cell.value}
                        siteId={String(site?.siteId)}
                        dataTestId={`repeater-image-${rowIndex}-${cell.column.id}`}
                        onChange={cdnUrl =>
                          handleCellChange(rowIndex, cell.column.id, cdnUrl)
                        }
                      />
                    ) : (
                      <div
                        contentEditable
                        suppressContentEditableWarning
                        onBlur={e =>
                          handleCellChange(
                            rowIndex,
                            cell.column.id,
                            e.target.innerText
                          )
                        }
                      >
                        {cell.value}
                      </div>
                    )}
                  </StyledTableCellBody>
                ))}
              </TableRow>
            );
          })}
          <TableRow>
            <StyledTableCellHeader colSpan={columns.length + 1}>
              <div
                data-test-id="repeater-add-row"
                onClick={addRow}
                style={{ cursor: 'pointer', fontWeight: 'bold' }}
              >
                {' '}
                + Add Row
              </div>
            </StyledTableCellHeader>
          </TableRow>
        </TableBody>
      </Table>

      <Menu
        anchorEl={rowMenuAnchor}
        open={Boolean(rowMenuAnchor)}
        onClose={handleCloseMenu}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <MenuList
          style={{ display: 'flex', flexDirection: 'column', padding: '8px' }}
        >
          <StyledMenuItem data-test-id="repeater-insert-row" onClick={addRow}>
            Insert Row
          </StyledMenuItem>
          <StyledMenuItem
            data-test-id="repeater-delete-row"
            onClick={deleteRow}
          >
            Delete Row
          </StyledMenuItem>
          <StyledMenuItem
            data-test-id="repeater-move-row-up"
            onClick={moveRowUp}
          >
            Move Row Up
          </StyledMenuItem>
          <StyledMenuItem
            data-test-id="repeater-move-row-down"
            onClick={moveRowDown}
          >
            Move Row Down
          </StyledMenuItem>
          <StyledMenuItem onClick={addColumn} disabled>
            Insert Column
          </StyledMenuItem>
          <StyledMenuItem onClick={deleteColumn} disabled>
            Delete Column
          </StyledMenuItem>
          <StyledMenuItem onClick={moveColumnLeft} disabled>
            Move Column Left
          </StyledMenuItem>
          <StyledMenuItem onClick={moveColumnRight} disabled>
            Move Column Right
          </StyledMenuItem>
        </MenuList>
      </Menu>

      <Menu
        anchorEl={columnMenuAnchor}
        open={Boolean(columnMenuAnchor)}
        onClose={handleCloseMenu}
      >
        <MenuList
          style={{ display: 'flex', flexDirection: 'column', padding: '8px' }}
        >
          <StyledMenuItem
            data-test-id="repeater-insert-column"
            onClick={addColumn}
          >
            Insert Column
          </StyledMenuItem>
          <StyledMenuItem
            data-test-id="repeater-delete-column"
            onClick={deleteColumn}
          >
            Delete Column
          </StyledMenuItem>
          <StyledMenuItem
            data-test-id="repeater-move-column-left"
            onClick={moveColumnLeft}
          >
            Move Column Left
          </StyledMenuItem>
          <StyledMenuItem
            data-test-id="repeater-move-column-right"
            onClick={moveColumnRight}
          >
            Move Column Right
          </StyledMenuItem>
          <StyledMenuItem
            data-test-id="repeater-toggle-image-column"
            onClick={() => {
              if (selectedColumnIndex == null) return;
              const columnName = columns[selectedColumnIndex]?.accessor;
              if (!columnName) return;
              const isImage = imageColumns.includes(columnName);
              if (isImage) {
                onImageColumnsChange?.(
                  imageColumns.filter(col => col !== columnName)
                );
              } else {
                onImageColumnsChange?.([...imageColumns, columnName]);
              }
              handleCloseMenu();
            }}
          >
            {selectedColumnIndex != null &&
            imageColumns.includes(columns[selectedColumnIndex]?.accessor)
              ? 'Unmark as Image Column'
              : 'Mark as Image Column'}
          </StyledMenuItem>
          <StyledMenuItem onClick={addRow} disabled>
            Insert Row
          </StyledMenuItem>
          <StyledMenuItem onClick={deleteRow} disabled>
            Delete Row
          </StyledMenuItem>
          <StyledMenuItem onClick={moveRowUp} disabled>
            Move Row Up
          </StyledMenuItem>
          <StyledMenuItem onClick={moveRowDown} disabled>
            Move Row Down
          </StyledMenuItem>
        </MenuList>
      </Menu>
    </StylesProvider>
  );
};

export default RepeaterDataTable;
